import { useQuery } from "@tanstack/react-query";
import { API_URL } from "src/Utils/API";

/**
 * One capability a tier grants, paired with the sentence the pricing page makes
 * of it. `perkDescriptionKey` is `null` for a capability the plan grants without
 * selling — the restaurant page, the opening hours — and a capability can be
 * listed twice under two marketing angles (full booking management and the
 * no-show blacklist are both `booking_management`); it is still granted once.
 */
export interface TierCapability {
  name: string;
  perkDescriptionKey: string | null;
}

/**
 * A subscription plan from the public catalog. The catalog is backend-driven:
 * the UI does not know the set of tiers or capabilities ahead of time and
 * resolves every line of copy from the translation keys the catalog carries.
 *
 * Two prices, both per month and both HT: `monthlyPrice` is what a month costs
 * on monthly billing, `annualMonthlyPrice` the discounted per-month rate when
 * the year is paid upfront. `annualPrice` is the server-derived yearly total —
 * the rate is what the pricing page advertises, the total is what an annual
 * payment is actually charged. `popular` flags the highlighted plan.
 */
export interface SubscriptionTier {
  name: string;
  priority: number;
  monthlyPrice: number;
  annualMonthlyPrice: number;
  annualPrice: number;
  capabilities: TierCapability[];
  /**
   * Promises the tier makes that no capability gates — support commitments,
   * recurring giveaways, views bundled into a feature it already grants.
   * Translation keys, like `TierCapability.perkDescriptionKey`.
   */
  additionalPerks: string[];
  popular: boolean;
}

const subscriptionTiersQueryKey = ["subscriptions", "tiers"];

export const useGetSubscriptionTiers = () =>
  useQuery({
    queryKey: subscriptionTiersQueryKey,
    queryFn: async (): Promise<SubscriptionTier[]> => {
      const response = await fetch(`${API_URL}/subscriptions/tiers`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch subscription tiers");
      }

      return (await response.json()) as SubscriptionTier[];
    },
    staleTime: Infinity,
  });
